import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import healthApi from '@/api/healthApi';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, UserCheck, Shield, Lock, AlertCircle, Phone, Mail, Clock } from 'lucide-react';
import { toast } from 'sonner';

const specialistTypes = [
  {
    value: 'mental_health',
    label: 'Mental Health Support',
    icon: '🧠',
    description: 'Talk to a mental health professional about stress, anxiety, or emotional wellness'
  },
  {
    value: 'nutrition',
    label: 'Nutrition Support',
    icon: '🥗',
    description: 'Get personalized nutrition guidance and meal planning support'
  },
  {
    value: 'lifestyle_coaching',
    label: 'Lifestyle Coaching',
    icon: '🎯',
    description: 'Work with a coach on habits, goals, and sustainable lifestyle changes'
  },
  {
    value: 'general_wellness',
    label: 'General Wellness Support',
    icon: '💚',
    description: 'Comprehensive wellness guidance for overall health and prevention'
  }
];

const TalkToSpecialist = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [previousRequests, setPreviousRequests] = useState([]);
  const [formData, setFormData] = useState({
    specialist_type: '',
    user_name: user?.name || '',
    concern: '',
    preferred_time: '',
    contact_method: 'email',
    contact_details: user?.email || ''
  });

  useEffect(() => {
    fetchPreviousRequests();
  }, []);

  const fetchPreviousRequests = async () => {
    try {
      const { data } = await healthApi.getSpecialistRequests();
      setPreviousRequests(data);
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.specialist_type) {
      toast.error('Please select a specialist type');
      return;
    }

    if (!formData.concern) {
      toast.error('Please describe your concern');
      return;
    }

    setLoading(true);
    try {
      await healthApi.createSpecialistRequest(formData);
      toast.success('Your request has been submitted! A specialist will reach out soon.');
      setFormData({
        ...formData,
        concern: '',
        preferred_time: '',
        specialist_type: ''
      });
      fetchPreviousRequests();
    } catch (error) {
      toast.error('Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const getSpecialistLabel = (type) => {
    const specialist = specialistTypes.find(s => s.value === type);
    return specialist ? specialist.label : type;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      scheduled: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4" data-testid="specialist-page">
      <div className="max-w-5xl mx-auto pt-4 pb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-4"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Button>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <UserCheck className="h-8 w-8 mr-3 text-indigo-600" />
            Confidential Specialist Support
          </h1>
          <p className="text-gray-600 mt-2">Connect with wellness professionals for personalized, confidential guidance</p>
        </div>

        {/* Privacy & Confidentiality Banner */}
        <Card className="mb-6 border-2 border-indigo-200 bg-indigo-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="flex gap-2">
                <Shield className="h-6 w-6 text-indigo-600 flex-shrink-0" />
                <Lock className="h-6 w-6 text-indigo-600 flex-shrink-0" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-indigo-900 mb-2">Confidential Support</h3>
                <p className="text-sm text-indigo-800 leading-relaxed">
                  All consultations are completely confidential and secure. Our specialists provide personalized preventive guidance tailored to your wellness goals, from the comfort and privacy of your home.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Important Disclaimer */}
        <Card className="mb-6 border-2 border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-bold text-red-900 mb-2">Medical Disclaimer</h3>
                <p className="text-sm text-red-800 leading-relaxed">
                  HealthLoop provides preventive health intelligence and wellness support—not emergency medical services. If you're experiencing a medical emergency, please call emergency services immediately. This platform is designed for proactive care and non-urgent wellness consultations.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Request Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Request a Consultation</CardTitle>
                <CardDescription>Choose the type of support you need and share your concerns</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Specialist Type Selection */}
                  <div className="space-y-3">
                    <Label className="text-base font-semibold">Type of Support</Label>
                    <div className="grid grid-cols-1 gap-3">
                      {specialistTypes.map((specialist) => (
                        <button
                          key={specialist.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, specialist_type: specialist.value })}
                          className={`p-4 rounded-xl border-2 text-left transition-all ${
                            formData.specialist_type === specialist.value
                              ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                              : 'border-gray-200 hover:border-indigo-300 bg-white'
                          }`}
                          data-testid={`specialist-${specialist.value}`}
                        >
                          <div className="flex items-start gap-3">
                            <span className="text-2xl">{specialist.icon}</span>
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">{specialist.label}</p>
                              <p className="text-sm text-gray-600 mt-1">{specialist.description}</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name">Your Name</Label>
                    <Input
                      id="name"
                      value={formData.user_name}
                      onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
                      placeholder="Full name"
                      required
                      data-testid="name-input"
                    />
                  </div>

                  {/* Concern */}
                  <div className="space-y-2">
                    <Label htmlFor="concern">What would you like to discuss?</Label>
                    <Textarea
                      id="concern"
                      value={formData.concern}
                      onChange={(e) => setFormData({ ...formData, concern: e.target.value })}
                      placeholder="Briefly describe what you'd like support with. This information is confidential."
                      rows={4}
                      required
                      data-testid="concern-textarea"
                    />
                  </div>

                  {/* Preferred Time */}
                  <div className="space-y-2">
                    <Label htmlFor="time" className="flex items-center">
                      <Clock className="h-4 w-4 mr-2" />
                      Preferred Time/Date (Optional)
                    </Label>
                    <Input
                      id="time"
                      value={formData.preferred_time}
                      onChange={(e) => setFormData({ ...formData, preferred_time: e.target.value })}
                      placeholder="e.g., Weekday mornings, This Saturday afternoon"
                      data-testid="time-input"
                    />
                  </div>

                  {/* Contact Method */}
                  <div className="space-y-2">
                    <Label htmlFor="contact_method">Preferred Contact Method</Label>
                    <Select 
                      value={formData.contact_method} 
                      onValueChange={(value) => setFormData({ ...formData, contact_method: value })}
                    >
                      <SelectTrigger data-testid="contact-method-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">
                          <div className="flex items-center">
                            <Mail className="h-4 w-4 mr-2" />
                            Email
                          </div>
                        </SelectItem>
                        <SelectItem value="phone">
                          <div className="flex items-center">
                            <Phone className="h-4 w-4 mr-2" />
                            Phone
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Contact Details */}
                  <div className="space-y-2">
                    <Label htmlFor="contact_details">
                      {formData.contact_method === 'email' ? 'Email Address' : 'Phone Number'}
                    </Label>
                    <Input
                      id="contact_details"
                      type={formData.contact_method === 'email' ? 'email' : 'tel'}
                      value={formData.contact_details}
                      onChange={(e) => setFormData({ ...formData, contact_details: e.target.value })}
                      placeholder={formData.contact_method === 'email' ? 'your@email.com' : '+1 (555) 000-0000'}
                      required
                      data-testid="contact-details-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full py-6 text-lg bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                    disabled={loading}
                    data-testid="submit-request"
                  >
                    {loading ? 'Submitting...' : 'Submit Request'}
                  </Button>

                  <p className="text-xs text-gray-500 text-center">
                    By submitting, you agree that a qualified specialist will contact you using your preferred method.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Previous Requests */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle>Your Requests</CardTitle>
                <CardDescription>Track your consultation requests</CardDescription>
              </CardHeader>
              <CardContent>
                {previousRequests.length === 0 ? (
                  <div className="text-center py-8">
                    <UserCheck className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 text-sm">No requests yet</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto" data-testid="previous-requests">
                    {previousRequests.map((request) => (
                      <div key={request.id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <p className="font-semibold text-sm">{getSpecialistLabel(request.specialist_type)}</p>
                          <Badge className={getStatusColor(request.status)}>
                            {request.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-600 mb-1 line-clamp-2">{request.concern}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(request.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TalkToSpecialist;
